import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { setToken, getToken, setOnUnauthorized, events } from '@/api/client'
import type { StudentInfo, ClassInfo, TeacherInfo } from '@/api/client'

// ── Auth session storage ──────────────────────────────────────────────────────
// Auth state lives in sessionStorage, not localStorage: it dies with the tab.
// Students share Chromebooks, and a week-long token in localStorage meant the
// next kid to open the tab inherited the last one's session. Per-student
// preferences (avatar, a11y, realm-intro marks) stay in localStorage on purpose.
const AUTH_KEYS = ['bb_token', 'bb_student', 'bb_class', 'bb_teacher'] as const
const session = {
  get(key: string): string | null { try { return sessionStorage.getItem(key) } catch { return null } },
  set(key: string, value: string) { try { sessionStorage.setItem(key, value) } catch { /* private mode */ } },
  clear() { for (const k of AUTH_KEYS) { try { sessionStorage.removeItem(k) } catch { /* ignore */ } } },
}
// One-time cleanup: sessions saved by older builds in localStorage must not resume.
try { for (const k of AUTH_KEYS) localStorage.removeItem(k) } catch { /* ignore */ }

// ── Avatar customisation ──────────────────────────────────────────────────────
// All values are Phaser hex numbers (0xRRGGBB).
export interface AvatarConfig {
  skin:   number   // face / hands colour
  hair:   number   // grad cap colour (represents hair)
  outfit: number   // robe colour
}
export const DEFAULT_AVATAR: AvatarConfig = {
  skin:   0xfcd34d,  // warm yellow (original sprite)
  hair:   0x1e1b4b,  // dark indigo
  outfit: 0x4338ca,  // indigo robe
}

// ── Accessibility: text scale ────────────────────────────────────────────────
export const TEXT_SCALES    = [1, 1.35, 1.7] as const
export const TEXT_SCALE_LABELS = ['Normal', 'Large', 'X-Large'] as const
export type  TextScale      = typeof TEXT_SCALES[number]

interface GameContextValue {
  // Auth state
  student: StudentInfo | null
  classInfo: ClassInfo | null
  teacher: TeacherInfo | null
  isTeacher: boolean

  // Auth actions
  loginStudent: (student: StudentInfo, classInfo: ClassInfo, token: string) => void
  loginTeacher: (teacher: TeacherInfo, token: string) => void
  logout: () => void

  // Avatar
  avatar: AvatarConfig
  setAvatar: (a: AvatarConfig) => void

  // Accessibility
  textScale: TextScale
  textScaleIndex: number
  cycleTextScale: () => void
  setTextScaleIndex: (i: number) => void
  highContrast: boolean
  readableFont: boolean
  reduceMotion: boolean
  muteMusic: boolean
  setHighContrast: (v: boolean) => void
  setReadableFont: (v: boolean) => void
  setReduceMotion: (v: boolean) => void
  setMuteMusic: (v: boolean) => void

  // Game state
  currentBattle: { standardId: string; bossName: string; biome: string } | null
  setCurrentBattle: (b: { standardId: string; bossName: string; biome: string } | null) => void
  lastXPEarned: number
  setLastXPEarned: (xp: number) => void
}

const GameContext = createContext<GameContextValue | null>(null)

export function GameProvider({ children }: { children: ReactNode }) {
  const [student, setStudent]         = useState<StudentInfo | null>(() => {
    try { return JSON.parse(session.get('bb_student') || 'null') }
    catch { return null }
  })
  const [classInfo, setClassInfo]     = useState<ClassInfo | null>(() => {
    try { return JSON.parse(session.get('bb_class') || 'null') }
    catch { return null }
  })
  const [teacher, setTeacher]         = useState<TeacherInfo | null>(() => {
    try { return JSON.parse(session.get('bb_teacher') || 'null') }
    catch { return null }
  })
  const [avatar, setAvatarState] = useState<AvatarConfig>(() => {
    try { return JSON.parse(localStorage.getItem('bb_avatar') || 'null') ?? DEFAULT_AVATAR }
    catch { return DEFAULT_AVATAR }
  })
  const setAvatar = useCallback((a: AvatarConfig) => {
    localStorage.setItem('bb_avatar', JSON.stringify(a))
    setAvatarState(a)
  }, [])

  // Text scale — persisted across sessions
  const [textScaleIndex, setTextScaleIndex] = useState<number>(() => {
    const saved = parseInt(localStorage.getItem('bb_text_scale') || '-1', 10)
    return (saved >= 0 && saved < TEXT_SCALES.length) ? saved : 1  // default to Large (1.35×)
  })
  const cycleTextScale = useCallback(() => {
    setTextScaleIndex(i => {
      const next = (i + 1) % TEXT_SCALES.length
      localStorage.setItem('bb_text_scale', String(next))
      return next
    })
  }, [])
  const setTextScaleIndexPersisted = useCallback((i: number) => {
    const clamped = Math.max(0, Math.min(TEXT_SCALES.length - 1, i))
    localStorage.setItem('bb_text_scale', String(clamped))
    setTextScaleIndex(clamped)
  }, [])

  // ── Accessibility toggles — persisted, opt-in ───────────────────────────────
  const boolPref = (key: string) => {
    try { return localStorage.getItem(key) === '1' } catch { return false }
  }
  const [highContrast, setHighContrastState] = useState<boolean>(() => boolPref('bb_high_contrast'))
  const [readableFont, setReadableFontState] = useState<boolean>(() => boolPref('bb_readable_font'))
  const [reduceMotion, setReduceMotionState] = useState<boolean>(() => boolPref('bb_reduce_motion'))
  const [muteMusic, setMuteMusicState]       = useState<boolean>(() => boolPref('bb_mute_music'))

  const setHighContrast = useCallback((v: boolean) => {
    localStorage.setItem('bb_high_contrast', v ? '1' : '0'); setHighContrastState(v)
  }, [])
  const setReadableFont = useCallback((v: boolean) => {
    localStorage.setItem('bb_readable_font', v ? '1' : '0'); setReadableFontState(v)
  }, [])
  const setReduceMotion = useCallback((v: boolean) => {
    localStorage.setItem('bb_reduce_motion', v ? '1' : '0'); setReduceMotionState(v)
  }, [])
  const setMuteMusic = useCallback((v: boolean) => {
    localStorage.setItem('bb_mute_music', v ? '1' : '0'); setMuteMusicState(v)
  }, [])

  // Reflect accessibility prefs as classes on <html> so global CSS can respond.
  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('a11y-high-contrast', highContrast)
    root.classList.toggle('a11y-readable', readableFont)
    root.classList.toggle('a11y-reduce-motion', reduceMotion)
  }, [highContrast, readableFont, reduceMotion])

  const [currentBattle, setCurrentBattle] = useState<GameContextValue['currentBattle']>(null)
  const [lastXPEarned, setLastXPEarned]   = useState(0)

  // Restore token on load
  const storedToken = session.get('bb_token')
  if (storedToken && !getToken()) setToken(storedToken)

  // A restored session (token survived a page load) counts as a returning visit.
  // Fresh logins are tracked in loginStudent/loginTeacher instead.
  useEffect(() => {
    if (getToken() && (student || teacher)) events.track('session_open')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loginStudent = useCallback((s: StudentInfo, c: ClassInfo, token: string) => {
    setToken(token)
    session.clear() // never layer a student session on a stale teacher one (or vice versa)
    session.set('bb_token',   token)
    session.set('bb_student', JSON.stringify(s))
    session.set('bb_class',   JSON.stringify(c))
    setStudent(s)
    setClassInfo(c)
    events.track('login')
  }, [])

  const loginTeacher = useCallback((t: TeacherInfo, token: string) => {
    setToken(token)
    session.clear()
    session.set('bb_token',   token)
    session.set('bb_teacher', JSON.stringify(t))
    setTeacher(t)
    events.track('login')
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    session.clear()
    setStudent(null)
    setClassInfo(null)
    setTeacher(null)
    setCurrentBattle(null)
  }, [])

  // If any authenticated request 401s (expired token from a restored session),
  // log out so the router shows the login screen instead of an empty world.
  useEffect(() => {
    setOnUnauthorized(logout)
    return () => setOnUnauthorized(null)
  }, [logout])

  return (
    <GameContext.Provider value={{
      student, classInfo, teacher,
      isTeacher: teacher !== null,
      loginStudent, loginTeacher, logout,
      avatar, setAvatar,
      textScale: TEXT_SCALES[textScaleIndex],
      textScaleIndex,
      cycleTextScale,
      setTextScaleIndex: setTextScaleIndexPersisted,
      highContrast, readableFont, reduceMotion, muteMusic,
      setHighContrast, setReadableFont, setReduceMotion, setMuteMusic,
      currentBattle, setCurrentBattle,
      lastXPEarned, setLastXPEarned,
    }}>
      {children}
    </GameContext.Provider>
  )
}

export function useGame() {
  const ctx = useContext(GameContext)
  if (!ctx) throw new Error('useGame must be used within GameProvider')
  return ctx
}
